import { GRID_SIZE } from "./constants.ts";

export type AlignMode =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "centerX"
  | "centerY";

export type DistributeMode = "horizontal" | "vertical";

export type AlignTarget = { id: string; x: number; y: number };

function snap(n: number): number {
  return Math.round(n / GRID_SIZE) * GRID_SIZE;
}

/**
 * Returnerar nya positioner för alla targets givet alignment-mode. Positionen
 * är mätt till symbolens centrum (samma som React Flow position med
 * nodeOrigin=[0.5,0.5]), så "align left" = minsta x bland urvalet.
 * No-op för < 2 targets — returnerar tom array så att anroparen kan skippa
 * hela mutate-batch utan special-casing.
 */
export function computeAlignment(
  targets: ReadonlyArray<AlignTarget>,
  mode: AlignMode,
): AlignTarget[] {
  if (targets.length < 2) return [];
  switch (mode) {
    case "left": {
      const x = snap(Math.min(...targets.map((t) => t.x)));
      return targets.map((t) => ({ ...t, x }));
    }
    case "right": {
      const x = snap(Math.max(...targets.map((t) => t.x)));
      return targets.map((t) => ({ ...t, x }));
    }
    case "top": {
      const y = snap(Math.min(...targets.map((t) => t.y)));
      return targets.map((t) => ({ ...t, y }));
    }
    case "bottom": {
      const y = snap(Math.max(...targets.map((t) => t.y)));
      return targets.map((t) => ({ ...t, y }));
    }
    case "centerX": {
      // Centra till urvalets geometriska mittpunkt — inte bounding-box-centrum.
      // Bounding-box skulle drifta när enstaka out-liers finns; medelvärde är
      // mer förutsägbart för det typiska fallet "tre symboler i rad".
      const x = snap(average(targets.map((t) => t.x)));
      return targets.map((t) => ({ ...t, x }));
    }
    case "centerY": {
      const y = snap(average(targets.map((t) => t.y)));
      return targets.map((t) => ({ ...t, y }));
    }
  }
}

/**
 * Fördela targets jämnt mellan ytter-targetsarna. Behåller de två ytter-
 * positionerna orörda och distribuerar de mellanliggande med lika stort
 * avstånd. Kräver minst 3 targets — färre är per definition redan
 * "jämnt fördelade".
 */
export function computeDistribution(
  targets: ReadonlyArray<AlignTarget>,
  mode: DistributeMode,
): AlignTarget[] {
  if (targets.length < 3) return [];
  const axis = mode === "horizontal" ? "x" : "y";
  const sorted = [...targets].sort((a, b) => a[axis] - b[axis]);
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  const step = (last[axis] - first[axis]) / (sorted.length - 1);
  const out: AlignTarget[] = [];
  sorted.forEach((t, i) => {
    if (i === 0 || i === sorted.length - 1) {
      out.push({ ...t });
      return;
    }
    const newVal = snap(first[axis] + step * i);
    out.push(axis === "x" ? { ...t, x: newVal } : { ...t, y: newVal });
  });
  return out;
}

function average(vals: ReadonlyArray<number>): number {
  if (vals.length === 0) return 0;
  let sum = 0;
  for (const v of vals) sum += v;
  return sum / vals.length;
}
